"use client";

/**
 * @file ダッシュボードのソート可能なサイドバーコンポーネント
 * 統合基底コンポーネントを使用した軽量な実装
 */

import Link from "next/link";
import { SidebarMenuItem } from "@/components/ui/sidebar";
import { DashboardSidebarGroupItem } from "./dashboard-sidebar-group-item";
import DragOverlayDashboard from "./drag-overlay-dashboard";
import SidebarMenuButtonWithPath from "./sidebar-menu-button-with-path";
import { SortableSidebarGroupsBase } from "./sortable-sidebar-groups-base";
import type { Dashboard } from "./types";

interface SortableDashboardsProps {
  workspaceId: string;
  dashboards: readonly Dashboard[];
  dashboardOrder?: readonly string[] | null;
  currentPathname: string;
  isReorderEnabled?: boolean;
}

/**
 * ダッシュボード用ソート可能サイドバーコンポーネント
 * 統合基底コンポーネントを使用してコードの重複を削減
 */
export default function SortableDashboards({
  workspaceId,
  dashboards,
  currentPathname,
  isReorderEnabled = true,
}: SortableDashboardsProps) {
  // ダッシュボード専用のレンダリング関数
  const renderDashboardItem = (
    dashboard: Dashboard & { isProjectActive: boolean },
    isDragging: boolean,
    isUpdating: boolean,
  ) => {
    const pathPattern = `.*/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(dashboard.id)}$`;
    const linkPath = `/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(dashboard.id)}`;

    const linkClassName = isReorderEnabled
      ? isDragging
        ? "cursor-grabbing"
        : "cursor-grab"
      : "cursor-pointer";

    return (
      <SidebarMenuItem className="sticky top-[calc(var(--sidebar-sticky-top,0px)+var(--sidebar-sticky-level-1))] z-10 bg-sidebar">
        <SidebarMenuButtonWithPath
          activePattern={pathPattern}
          className="w-full"
          asChild
        >
          <Link
            href={linkPath}
            className={linkClassName}
            onDragStartCapture={(e) => {
              e.preventDefault();
            }}
            onClick={() => {
              // ダッシュボードIDをクッキーに保存
              document.cookie = `last_dashboard_id_${encodeURIComponent(
                workspaceId,
              )}=${encodeURIComponent(dashboard.id)}; path=/`;
            }}
          >
            <DashboardSidebarGroupItem
              name={dashboard.name}
              isDragging={isReorderEnabled ? isDragging : false}
            />
          </Link>
        </SidebarMenuButtonWithPath>
      </SidebarMenuItem>
    );
  };

  // DragOverlay専用のレンダリング関数
  const renderDragOverlayItem = (
    dashboard: Dashboard & { isProjectActive: boolean },
  ) => {
    const pathPattern = `.*/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(dashboard.id)}$`;
    const linkPath = `/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(dashboard.id)}`;

    return (
      <DragOverlayDashboard
        dashboardName={dashboard.name}
        linkHref={linkPath}
        activePattern={pathPattern}
      />
    );
  };

  return (
    <SortableSidebarGroupsBase
      workspaceId={workspaceId}
      items={dashboards as Dashboard[]}
      projectType="dashboards"
      currentPathname={currentPathname}
      renderItem={renderDashboardItem}
      renderDragOverlayItem={renderDragOverlayItem}
      disableSorting={!isReorderEnabled}
    />
  );
}
